import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    useTranslate,
    SaveButton,
    Toolbar,
    useRecordContext,
    FormDataConsumer,
    useInput,
    useRedirect,
    useNotify,
    useDataProvider,
} from 'react-admin';
import { Box, Typography, Alert, Card, CardContent, TextField, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import { ArrowBack, Schedule } from '@mui/icons-material';
import { EntitySelector } from './components/EntitySelector';
import { useTimezone } from '../../contexts/TimezoneContext';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const FEE_SCHEDULE_KNOWN_FIELDS = new Set([
    'name', 'description', 'templateId', 'entityType', 'entityId',
    'priority', 'feeStructure', 'currency', 'effectiveFrom',
    'effectiveUntil', 'isActive',
]);

const mapFeeScheduleBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const feeScheduleFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return FEE_SCHEDULE_KNOWN_FIELDS.has(head)
        ? `resources.fee-schedules.fields.${head}`
        : field;
};

// Custom DateTime Input with 24-hour format
const CustomDateTimeInput = ({ source, label, validate, helperText, sx, disabled, ...props }: any) => {
    const {
        field: { value, onChange },
        fieldState: { error },
    } = useInput({ source, validate });
    
    const [dateValue, setDateValue] = useState('');
    const [timeValue, setTimeValue] = useState('');
    
    // Initialize date and time values when value changes
    React.useEffect(() => {
        if (value) {
            const date = new Date(value);
            setDateValue(date.toISOString().slice(0, 10)); // YYYY-MM-DD
            setTimeValue(date.toTimeString().slice(0, 5)); // HH:MM
        } else {
            setDateValue('');
            setTimeValue('');
        }
    }, [value]);
    
    // Update the form value when date or time changes
    const updateDateTime = (newDate: string, newTime: string) => {
        if (newDate && newTime) {
            const dateTime = new Date(`${newDate}T${newTime}:00`);
            onChange(dateTime.toISOString());
        } else if (newDate && !newTime) {
            const dateTime = new Date(`${newDate}T00:00:00`);
            onChange(dateTime.toISOString());
        } else {
            onChange('');
        }
    };
    
    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = event.target.value;
        setDateValue(newDate);
        updateDateTime(newDate, timeValue);
    };
    
    const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = event.target.value;
        setTimeValue(newTime);
        updateDateTime(dateValue, newTime);
    };
    
    return (
        <Box sx={sx}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    {...props}
                    label="Date"
                    type="date"
                    value={dateValue}
                    onChange={handleDateChange}
                    error={!!error}
                    disabled={disabled}
                    size="small"
                    sx={{ flex: 1 }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
                    <FormControl size="small" sx={{ minWidth: 60 }} disabled={disabled}>
                        <InputLabel sx={{ fontSize: '0.75rem' }}>Hour</InputLabel>
                        <Select
                            value={timeValue.split(':')[0] || '00'}
                            onChange={(e) => {
                                const hour = e.target.value;
                                const minute = timeValue.split(':')[1] || '00';
                                const newTime = `${hour}:${minute}`;
                                setTimeValue(newTime);
                                updateDateTime(dateValue, newTime);
                            }}
                            label="Hour"
                            error={!!error}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#E0E0E0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C853',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C853',
                                },
                                '& .MuiSelect-select': {
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                },
                            }}
                        >
                            {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                                <MenuItem key={hour} value={hour} sx={{ fontFamily: 'monospace' }}>
                                    {hour}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', mx: 0.5 }}>:</Typography>
                    <FormControl size="small" sx={{ minWidth: 60 }} disabled={disabled}>
                        <InputLabel sx={{ fontSize: '0.75rem' }}>Min</InputLabel>
                        <Select
                            value={timeValue.split(':')[1] || '00'}
                            onChange={(e) => {
                                const hour = timeValue.split(':')[0] || '00';
                                const minute = e.target.value;
                                const newTime = `${hour}:${minute}`;
                                setTimeValue(newTime);
                                updateDateTime(dateValue, newTime);
                            }}
                            label="Min"
                            error={!!error}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#E0E0E0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C853',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C853',
                                },
                                '& .MuiSelect-select': {
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                },
                            }}
                        >
                            {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map((minute) => (
                                <MenuItem key={minute} value={minute} sx={{ fontFamily: 'monospace' }}>
                                    {minute}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
            {(error?.message || helperText) && (
                <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, display: 'block' }}>
                    {error?.message || helperText}
                </Typography>
            )}
        </Box>
    );
};


const EditToolbar = () => {
    const redirect = useRedirect();
    const notify = useNotify();

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton 
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const record = useRecordContext();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/fee-schedules')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    flexShrink: 0,
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.fee-schedules.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.fee-schedules.edit.title', { name: record?.name || '' })}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.fee-schedules.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const FeeScheduleEditForm = ({ serverErrors }: { serverErrors: Record<string, string> }) => {
    const translate = useTranslate();
    const { formatDate } = useTimezone();
    const record = useRecordContext();

    return (
        <SimpleForm
            toolbar={<EditToolbar />}
            sx={{
                '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                    px: { xs: 0, sm: 2 },
                    py: { xs: 1, sm: 2 },
                },
                padding: { xs: 0, sm: 'inherit' },
                '& .MuiPaper-root': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <ServerErrorBinder errors={serverErrors} />
            <FormDataConsumer>
                {({ formData }) => {
                    const hasAdjustments = formData?.adjustments && formData.adjustments.length > 0;
                    const isActive = formData?.isActive;

                    return (
                        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                            <PageHeader />

                        {hasAdjustments && (
                            <Alert severity="warning" sx={{ mb: { xs: 2, sm: 3 } }}>
                                <Typography variant="body2">
                                    {translate('resources.fee-schedules.edit.hasAdjustmentsWarning')}
                                </Typography>
                            </Alert>
                        )}

                        {/* Basic Information Card */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Schedule sx={{ color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.edit.basicInfo')}
                                    </Typography>
                                </Box>

                                <TextInput
                                    source="name"
                                    label={translate('resources.fee-schedules.fields.name')}
                                    validate={[required()]}
                                    fullWidth
                                    helperText={translate('resources.fee-schedules.edit.nameHelper')}
                                    sx={{ mb: 3 }}
                                />

                                <EntitySelector
                                    source="entity"
                                    showHierarchy
                                    disabled={hasAdjustments}
                                />

                                {hasAdjustments && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        {translate('resources.fee-schedules.edit.entityChangeDisabled')}
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>

                        {/* Schedule Configuration Card */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Schedule sx={{ color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.edit.scheduleConfig')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                                    <CustomDateTimeInput
                                        source="effectiveFrom"
                                        label={translate('resources.fee-schedules.fields.effectiveFrom')}
                                        validate={[required()]}
                                        helperText={translate('resources.fee-schedules.edit.effectiveDateHelper')}
                                        sx={{ flex: 1 }}
                                        disabled={hasAdjustments}
                                        fullWidth
                                    />
                                    <CustomDateTimeInput
                                        source="effectiveUntil"
                                        label={translate('resources.fee-schedules.fields.effectiveUntil')}
                                        helperText={translate('resources.fee-schedules.edit.expiryDateHelper')}
                                        sx={{ flex: 1 }}
                                        disabled={hasAdjustments}
                                        fullWidth
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                                    <WheelSafeNumberInput
                                        source="priority"
                                        label={translate('resources.fee-schedules.fields.priority')}
                                        helperText={translate('resources.fee-schedules.fields.priorityHelper')}
                                        validate={[required()]}
                                        min={1}
                                        max={1000}
                                        sx={{ flex: 1 }}
                                    />
                                    <BooleanInput
                                        source="isActive"
                                        label={translate('resources.fee-schedules.fields.active')}
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Fee Configuration Card */}
                        <Card sx={{ overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Schedule sx={{ color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.edit.feeConfig')}
                                    </Typography>
                                </Box>

                                <TemplateSelector source="templateId" />

                                {formData?.templateId && formData?.template && (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="info">
                                            {translate('resources.fee-schedules.edit.templateInfo')}
                                        </Alert>
                                        {formData.template.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                {formData.template.description}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                <Box sx={{ mt: 3 }}>
                                    {!hasAdjustments && (
                                        <>
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                {translate('resources.fee-schedules.edit.priorityInfo')}
                                            </Alert>
                                            <Alert severity="warning">
                                                {translate('resources.fee-schedules.edit.overlapWarning')}
                                            </Alert>
                                        </>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Status Information Card */}
                        {formData && (
                            <Card sx={{ mt: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.edit.statusInfo')}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, minWidth: 0 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                {translate('resources.fee-schedules.fields.createdAt')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                {formatDate(formData.createdAt, 'datetime')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, minWidth: 0 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                {translate('resources.fee-schedules.fields.updatedAt')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                {formatDate(formData.updatedAt, 'datetime')}
                                            </Typography>
                                        </Box>
                                        {formData.createdBy && (
                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                    {translate('resources.fee-schedules.fields.createdBy')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                    {formData.createdBy}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                );
            }}
            </FormDataConsumer>
        </SimpleForm>
    );
};

const TemplateSelector = ({ source }: { source: string }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const [templates, setTemplates] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('fee-templates', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: { isActive: true },
            });
            setTemplates(data);
        } catch (error) {
            console.error('Failed to load templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const templateChoices = templates.map(template => ({
        id: template.id,
        name: `${template.name} (${template.currency})`,
    }));

    return (
        <SelectInput
            source={source}
            label={translate('resources.fee-schedules.fields.template')}
            choices={templateChoices}
            validate={[required()]}
            disabled={loading}
            helperText={translate('resources.fee-schedules.fields.templateHelper')}
        />
    );
};

export const FeeSchedulesEdit = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    const notify = useNotify();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.fee-schedules.notifications.updated', { type: 'success' });
                    redirect('/fee-schedules');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapFeeScheduleBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(feeScheduleFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            transform={(data) => {
                // Pass through entityType/entityId without silent fallbacks. If the
                // user clears the entity, send the empty values and let the server
                // reject the update — silently retargeting to 'site' here would
                // corrupt billing for whichever site that resolves to.
                const { entity, ...rest } = data;
                return {
                    ...rest,
                    entityType: entity?.type,
                    entityId: entity?.id,
                };
            }}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <FeeScheduleEditForm serverErrors={serverErrors} />
        </Edit>
    );
};